import type {
  DocumentDto,
  DocumentValidationReviewDto,
  DocumentValueDto,
  DocumentValueItemSiteModulesInput,
  ModuleDto,
  MyWorkSiteDto,
} from '@app/contracts';
import { AlertTriangle, Building2, Info, Layers, Plus, Save, Wand2, X } from 'lucide-react';
import type { KeyboardEvent } from 'react';
import { useEffect, useMemo, useState } from 'react';
import { useIsoModulesForSiteIds } from '../../../modules/hooks/useModules.js';
import { useMySites } from '../../../progression/hooks/useProgression.js';
import { useCreateDocumentValue, useUpdateDocumentValue } from '../../hooks/useDocuments.js';
import { DocumentRightsButton } from '../DocumentRightsButton.js';
import { DocumentSubmitButton } from '../DocumentSubmitButton.js';
import { InlineDocumentValueValidation } from '../InlineDocumentValueValidation.js';
import {
  type RegistryRowModule,
  type RegistryRowSite,
  mergeMatrixItemPerimeterFromDto,
  normalizeRowModules,
  normalizeRowSites,
} from './RegistryModel.js';

interface PestelItem {
  clientKey: string;
  title: string;
  description: string;
  link: string;
  sites: RegistryRowSite[];
  modules: RegistryRowModule[];
}
type PestelField =
  | 'politique'
  | 'economique'
  | 'social'
  | 'technologique'
  | 'environnemental'
  | 'legal';
interface PestelData {
  politique: PestelItem[];
  economique: PestelItem[];
  social: PestelItem[];
  technologique: PestelItem[];
  environnemental: PestelItem[];
  legal: PestelItem[];
}

interface Props {
  document: DocumentDto;
  value: DocumentValueDto | undefined;
  readOnly?: boolean;
  highlightItemKeys?: Set<string>;
  validationReview?: DocumentValidationReviewDto;
}

const BADGE_BASE =
  'inline-flex max-w-full shrink-0 items-center gap-0.5 rounded-full border px-2 py-0.5 text-[11px] font-medium leading-none';

function sitesLabel(count: number): string {
  if (count === 0) return 'Aucun site';
  if (count === 1) return '1 site';
  return `${count} sites`;
}

function modulesLabel(count: number): string {
  if (count === 0) return 'Aucun module';
  if (count === 1) return '1 module';
  return `${count} modules`;
}

function normalizePestelItem(raw: unknown): PestelItem {
  const empty = (): PestelItem => ({
    clientKey: crypto.randomUUID(),
    title: '',
    description: '',
    link: '',
    sites: [],
    modules: [],
  });
  if (!raw || typeof raw !== 'object') return empty();
  const item = raw as Record<string, unknown>;
  return {
    clientKey:
      typeof item.clientKey === 'string' && item.clientKey.length > 0
        ? item.clientKey
        : crypto.randomUUID(),
    title: (item.title as string) || '',
    description: (item.description as string) || '',
    link: (item.link as string) || '',
    sites: normalizeRowSites(item.sites),
    modules: normalizeRowModules(item.modules),
  };
}

function parsePestelData(
  s: unknown,
  itemSiteModules: DocumentValueDto['itemSiteModules'] | undefined,
): PestelData {
  const byRef = new Map(
    (itemSiteModules ?? []).map(
      (x) => [x.itemRef, { sites: x.sites, modules: x.modules }] as const,
    ),
  );
  if (!s || typeof s !== 'object') {
    return {
      politique: [],
      economique: [],
      social: [],
      technologique: [],
      environnemental: [],
      legal: [],
    };
  }
  const r = s as Record<string, unknown>;
  const norm = (v: unknown) =>
    ((Array.isArray(v) ? v : []) as unknown[]).map((raw) =>
      mergeMatrixItemPerimeterFromDto(normalizePestelItem(raw), byRef),
    );
  return {
    politique: norm(r.Politique ?? r.politique),
    economique: norm(r.Economique ?? r.economique),
    social: norm(r.Social ?? r.social),
    technologique: norm(r.Technologique ?? r.technologique),
    environnemental: norm(r.Environnemental ?? r.environnemental),
    legal: norm(r.Legal ?? r.legal),
  };
}

function itemHasContent(it: PestelItem): boolean {
  return (
    String(it.title || '').trim() !== '' ||
    String(it.description || '').trim() !== '' ||
    String(it.link || '').trim() !== ''
  );
}

function validatePestelForSave(
  data: PestelData,
  sections: { field: PestelField; label: string }[],
  strictValidation: boolean,
): string[] {
  const err: string[] = [];
  for (const { field, label } of sections) {
    data[field].forEach((item, i) => {
      if (!itemHasContent(item)) return;
      if (item.sites.length < 1) {
        err.push(`${label}, élément ${i + 1} : sélectionnez au moins un site concerné.`);
      }
      if (strictValidation && !String(item.title || '').trim()) {
        err.push(`${label}, élément ${i + 1} : le titre est obligatoire.`);
      }
    });
  }
  return err;
}

const SECTIONS: { field: PestelField; label: string; color: string }[] = [
  { field: 'politique', label: 'Politique', color: 'bg-blue-50 border-blue-200' },
  { field: 'economique', label: 'Économique', color: 'bg-green-50 border-green-200' },
  { field: 'social', label: 'Social', color: 'bg-orange-50 border-orange-200' },
  { field: 'technologique', label: 'Technologique', color: 'bg-purple-50 border-purple-200' },
  { field: 'environnemental', label: 'Environnemental', color: 'bg-emerald-50 border-emerald-200' },
  { field: 'legal', label: 'Légal', color: 'bg-rose-50 border-rose-200' },
];

const PESTEL_FIELD_SERIAL: Record<PestelField, string> = {
  politique: 'Politique',
  economique: 'Economique',
  social: 'Social',
  technologique: 'Technologique',
  environnemental: 'Environnemental',
  legal: 'Legal',
};

export function ToolPestel({
  document,
  value,
  readOnly = false,
  highlightItemKeys,
  validationReview,
}: Props) {
  const { data: workSites, isLoading: sitesLoading } = useMySites();
  const workSiteIds = useMemo(() => workSites?.map((s) => s.siteId) ?? [], [workSites]);
  const { modules: moduleChoices, isLoading: modulesLoading } =
    useIsoModulesForSiteIds(workSiteIds);

  const [data, setData] = useState<PestelData>(() =>
    parsePestelData(value?.serialization, value?.itemSiteModules),
  );
  const [hasUnsaved, setHasUnsaved] = useState(false);
  const [editItem, setEditItem] = useState<PestelItem | null>(null);
  const [editField, setEditField] = useState<PestelField | null>(null);
  const [editIndex, setEditIndex] = useState<number | null>(null);
  const [showModal, setShowModal] = useState(false);
  const [saveStatus, setSaveStatus] = useState<'idle' | 'saving' | 'saved' | 'error'>('idle');

  const createValue = useCreateDocumentValue(document.id);
  const updateValue = useUpdateDocumentValue(document.id);

  useEffect(() => {
    setData(parsePestelData(value?.serialization, value?.itemSiteModules));
    setHasUnsaved(false);
  }, [value?.serialization, value?.itemSiteModules]);
  useEffect(() => {
    const h = (e: BeforeUnloadEvent) => {
      if (hasUnsaved) {
        e.preventDefault();
        e.returnValue = '';
      }
    };
    window.addEventListener('beforeunload', h);
    return () => window.removeEventListener('beforeunload', h);
  }, [hasUnsaved]);

  const handleSave = async () => {
    const validationErrors = validatePestelForSave(data, SECTIONS, document.strictValidation);
    if (validationErrors.length > 0) {
      alert(validationErrors.join('\n'));
      return;
    }

    setSaveStatus('saving');
    const strip = (items: PestelItem[]) =>
      items.map(({ clientKey, title, description, link }) => ({
        clientKey,
        title,
        description,
        link,
      }));
    const serialization = {
      Politique: strip(data.politique),
      Economique: strip(data.economique),
      Social: strip(data.social),
      Technologique: strip(data.technologique),
      Environnemental: strip(data.environnemental),
      Legal: strip(data.legal),
    };
    const collectItemSiteModules = (d: PestelData): DocumentValueItemSiteModulesInput[] => {
      const acc: DocumentValueItemSiteModulesInput[] = [];
      const pushAll = (items: PestelItem[]) => {
        for (const it of items) {
          acc.push({
            itemRef: it.clientKey,
            siteIds: it.sites.map((s) => s.id),
            moduleIds: it.modules.map((m) => m.id),
          });
        }
      };
      pushAll(d.politique);
      pushAll(d.economique);
      pushAll(d.social);
      pushAll(d.technologique);
      pushAll(d.environnemental);
      pushAll(d.legal);
      return acc;
    };
    const itemSiteModules = collectItemSiteModules(data);
    try {
      if (value?.id) {
        await updateValue.mutateAsync({
          valueId: value.id,
          input: { serialization, itemSiteModules },
        });
      } else {
        await createValue.mutateAsync({ serialization, itemSiteModules });
      }
      setHasUnsaved(false);
      setSaveStatus('saved');
      setTimeout(() => setSaveStatus('idle'), 2000);
    } catch {
      setSaveStatus('error');
      setTimeout(() => setSaveStatus('idle'), 3000);
    }
  };

  const addItem = (field: PestelField) => {
    const item: PestelItem = {
      clientKey: crypto.randomUUID(),
      title: '',
      description: '',
      link: '',
      sites: [],
      modules: [],
    };
    setData((p) => ({ ...p, [field]: [...p[field], item] }));
    setEditItem(item);
    setEditField(field);
    setEditIndex(data[field].length);
    setShowModal(true);
    setHasUnsaved(true);
  };

  const removeItem = (field: PestelField, i: number) => {
    setData((p) => ({ ...p, [field]: p[field].filter((_, j) => j !== i) }));
    setHasUnsaved(true);
  };

  const openModal = (item: PestelItem, field: PestelField, i: number) => {
    setEditItem({
      ...item,
      sites: item.sites.map((s) => ({ ...s })),
      modules: item.modules.map((m) => ({ ...m })),
    });
    setEditField(field);
    setEditIndex(i);
    setShowModal(true);
  };

  const toggleEditingSite = (site: MyWorkSiteDto, selected: boolean) => {
    setEditItem((prev) => {
      if (!prev) return prev;
      const nextSites = selected
        ? [
            ...prev.sites.filter((s) => s.id !== site.siteId),
            {
              id: site.siteId,
              name: site.siteName,
              city: site.city ?? undefined,
              country: site.country ?? undefined,
            },
          ]
        : prev.sites.filter((s) => s.id !== site.siteId);
      return { ...prev, sites: nextSites };
    });
  };

  const toggleEditingModule = (mod: ModuleDto, selected: boolean) => {
    setEditItem((prev) => {
      if (!prev) return prev;
      const nextModules = selected
        ? [
            ...prev.modules.filter((m) => m.id !== mod.id),
            { id: mod.id, name: mod.name, code: mod.code },
          ]
        : prev.modules.filter((m) => m.id !== mod.id);
      return { ...prev, modules: nextModules };
    });
  };

  const saveModal = () => {
    if (editField !== null && editIndex !== null && editItem) {
      setData((p) => ({
        ...p,
        [editField]: p[editField].map((it, i) => (i === editIndex ? editItem : it)),
      }));
      setHasUnsaved(true);
    }
    setShowModal(false);
    setEditItem(null);
    setEditField(null);
    setEditIndex(null);
  };

  const DEMO_DATA: Record<
    PestelField,
    Array<{ title: string; description: string; link: string }>
  > = {
    politique: [
      {
        title: 'Aides publiques à la transition',
        description:
          'Les dispositifs régionaux et nationaux encouragent les investissements en conformité et innovation.',
        link: '',
      },
      {
        title: 'Stabilité du cadre institutionnel',
        description:
          'Un environnement politique prévisible facilite la planification à moyen terme des audits et certifications.',
        link: '',
      },
    ],
    economique: [
      {
        title: 'Pression sur les coûts des intrants',
        description:
          'La conjoncture peut impacter les budgets formation, audits externes et équipements de mesure.',
        link: '',
      },
      {
        title: 'Marché du travail tendu',
        description:
          'Le recrutement de profils QHSE ou qualité qualifiés devient plus concurrentiel et coûteux.',
        link: '',
      },
    ],
    social: [
      {
        title: 'Attentes RSE et transparence',
        description:
          'Clients et parties intéressées demandent plus de traçabilité sur les pratiques et impacts sociaux.',
        link: '',
      },
      {
        title: 'Équilibre vie pro / télétravail',
        description:
          'Les modes de travail hybrides influencent la communication interne et la diffusion des procédures.',
        link: '',
      },
    ],
    technologique: [
      {
        title: 'Numérisation documentaire',
        description:
          'Les GED et workflows électroniques transforment la preuve de conformité et les contrôles internes.',
        link: '',
      },
      {
        title: 'Risques cyber et résilience',
        description:
          'La sécurité des SI et la continuité d’activité deviennent des enjeux majeurs pour les dossiers qualité.',
        link: '',
      },
    ],
    environnemental: [
      {
        title: 'Exigences climat des donneurs d’ordre',
        description:
          'Les appels d’offres intègrent souvent des critères carbone et de bilan environnemental.',
        link: '',
      },
      {
        title: 'Gestion des déchets et ressources',
        description:
          'Réduction, tri et filières de valorisation impactent les procédés et les indicateurs de performance.',
        link: '',
      },
    ],
    legal: [
      {
        title: 'Alignement sur les révisions ISO',
        description:
          'Les mises à jour des normes de référence obligent à revoir périodiquement le système de management.',
        link: '',
      },
      {
        title: 'Évolution du droit du travail et de la santé',
        description:
          'Les obligations en prévention et reporting peuvent modifier les pratiques et les documents type.',
        link: '',
      },
    ],
  };

  const fillWithDemoData = () => {
    const siteRefs =
      workSites?.map((s) => ({
        id: s.siteId,
        name: s.siteName,
        city: s.city ?? undefined,
        country: s.country ?? undefined,
      })) ?? [];
    const moduleRefs = moduleChoices.map((m) => ({ id: m.id, name: m.name, code: m.code }));
    if (siteRefs.length === 0) {
      alert(
        'Aucun site n’est assigné à votre compte : les éléments démo n’auront pas de site. Affectez-vous à au moins un site, ou cochez les sites après coup.',
      );
    }
    if (moduleRefs.length === 0 && workSiteIds.length > 0 && !modulesLoading) {
      alert(
        'Aucun module ISO sur vos sites : les cases modules restent vides. Assignez des modules dans la page Modules.',
      );
    }
    const withKeys = (
      items: Array<{ title: string; description: string; link: string }>,
    ): PestelItem[] =>
      items.map((i) => ({
        clientKey: crypto.randomUUID(),
        ...i,
        sites: siteRefs.map((s) => ({ ...s })),
        modules: moduleRefs.map((m) => ({ ...m })),
      }));
    setData({
      politique: withKeys(DEMO_DATA.politique),
      economique: withKeys(DEMO_DATA.economique),
      social: withKeys(DEMO_DATA.social),
      technologique: withKeys(DEMO_DATA.technologique),
      environnemental: withKeys(DEMO_DATA.environnemental),
      legal: withKeys(DEMO_DATA.legal),
    });
    setShowModal(false);
    setEditItem(null);
    setEditField(null);
    setEditIndex(null);
    setHasUnsaved(true);
  };

  const demoButtonDisabled = sitesLoading || (workSiteIds.length > 0 && modulesLoading);

  const valueValidationRow =
    validationReview?.canValidate && value?.id
      ? validationReview.validations.find((v) => v.documentValueId === value.id)
      : undefined;

  return (
    <div>
      <div className="mb-4 flex items-start justify-between rounded-xl border border-slate-200 bg-slate-50 p-4">
        <div>
          <h1 className="text-lg font-bold text-slate-900">{document.name}</h1>
          <p className="mt-0.5 text-xs text-slate-500">
            Créé le {new Date(document.createdAt).toLocaleDateString('fr-FR')} · Sites et modules
            par élément (via l’icône info)
          </p>
          {hasUnsaved && (
            <span className="mt-1 inline-flex items-center gap-1 rounded-full bg-amber-100 px-2 py-0.5 text-xs font-medium text-amber-700">
              <AlertTriangle className="h-3 w-3" />
              Modifications non sauvegardées
            </span>
          )}
        </div>
        <div className="flex items-center gap-2">
          {!readOnly ? <DocumentSubmitButton document={document} /> : null}
          <DocumentRightsButton document={document} />
          {!readOnly ? (
            <>
              <button
                type="button"
                onClick={fillWithDemoData}
                disabled={demoButtonDisabled}
                title={
                  demoButtonDisabled
                    ? 'Chargement des sites ou des modules pour préremplir les cases…'
                    : undefined
                }
                className="flex items-center gap-2 rounded-lg border border-violet-200 bg-violet-50 px-4 py-2 text-sm font-medium text-violet-700 transition-colors hover:bg-violet-100 disabled:cursor-not-allowed disabled:opacity-60"
              >
                <Wand2 className="h-4 w-4" />
                Démo
              </button>
              <button
                type="button"
                onClick={handleSave}
                disabled={saveStatus === 'saving'}
                className={`flex items-center gap-2 rounded-lg px-4 py-2 text-sm font-medium transition-colors ${saveStatus === 'saved' ? 'bg-emerald-500 text-white' : saveStatus === 'error' ? 'bg-red-500 text-white' : hasUnsaved ? 'bg-amber-500 text-white hover:bg-amber-600' : 'bg-slate-200 text-slate-600 hover:bg-slate-300'}`}
              >
                <Save className="h-4 w-4" />
                {saveStatus === 'saving'
                  ? 'Sauvegarde…'
                  : saveStatus === 'saved'
                    ? 'Sauvegardé !'
                    : saveStatus === 'error'
                      ? 'Erreur'
                      : 'Sauvegarder'}
              </button>
            </>
          ) : null}
        </div>
      </div>

      {valueValidationRow && validationReview ? (
        <div className="mb-4">
          <InlineDocumentValueValidation
            documentId={document.id}
            canValidate={validationReview.canValidate}
            validationRow={valueValidationRow}
          />
        </div>
      ) : null}

      <div className="grid grid-cols-3 gap-4">
        {SECTIONS.map(({ field, label, color }) => (
          <div
            key={field}
            className={`rounded-xl border p-4 ${color}`}
            onMouseEnter={(e) => {
              const b = e.currentTarget.querySelector<HTMLButtonElement>('.add-btn');
              if (b) b.style.opacity = '1';
            }}
            onMouseLeave={(e) => {
              const b = e.currentTarget.querySelector<HTMLButtonElement>('.add-btn');
              if (b) b.style.opacity = '0';
            }}
          >
            <h3 className="mb-3 text-center text-sm font-bold uppercase tracking-wide text-slate-700 border-b border-slate-200 pb-2">
              {label}
            </h3>
            <div className="space-y-2 min-h-[80px]">
              {data[field].map((item, i) => {
                const hl =
                  highlightItemKeys?.has(`${PESTEL_FIELD_SERIAL[field]}:${item.clientKey}`) ??
                  false;
                return (
                  <div
                    key={item.clientKey}
                    className={`flex flex-col gap-1 border-b border-slate-100 pb-2 last:border-b-0 ${
                      hl ? 'rounded-md bg-amber-50/90 ring-2 ring-amber-300/80 px-1 -mx-1' : ''
                    }`}
                  >
                    <div className="flex items-center gap-1.5">
                      <span className="text-xs text-slate-400 w-4 shrink-0">{i + 1}.</span>
                      <span className="flex-1 text-sm text-slate-700 truncate min-w-0">
                        {item.title || '—'}
                      </span>
                      {!readOnly ? (
                        <>
                          <button
                            type="button"
                            className="text-slate-400 hover:text-slate-600 shrink-0"
                            title="Modifier (sites, modules…)"
                            onClick={() => openModal(item, field, i)}
                          >
                            <Info className="h-3.5 w-3.5" />
                          </button>
                          <button
                            type="button"
                            className="text-slate-400 hover:text-red-500 shrink-0"
                            onClick={() => removeItem(field, i)}
                            title="Supprimer"
                          >
                            <X className="h-3.5 w-3.5" />
                          </button>
                        </>
                      ) : null}
                    </div>
                    <div className="flex flex-wrap items-center gap-1.5 pl-6">
                      <span
                        className={`${BADGE_BASE} border-slate-200 bg-white text-slate-700`}
                        title="Sites associés"
                      >
                        <Building2 className="h-3 w-3 shrink-0 opacity-70" aria-hidden />
                        {sitesLabel(item.sites.length)}
                      </span>
                      <span
                        className={`${BADGE_BASE} border-slate-200 bg-white text-slate-700`}
                        title="Modules ISO associés"
                      >
                        <Layers className="h-3 w-3 shrink-0 opacity-70" aria-hidden />
                        {modulesLabel(item.modules.length)}
                      </span>
                    </div>
                  </div>
                );
              })}
            </div>
            {!readOnly ? (
              <button
                type="button"
                className="add-btn mt-2 mx-auto flex h-6 w-6 items-center justify-center rounded-full bg-slate-200 text-slate-500 hover:bg-emerald-500 hover:text-white transition-all"
                style={{ opacity: 0 }}
                onClick={() => addItem(field)}
                title="Ajouter"
              >
                <Plus className="h-3.5 w-3.5" />
              </button>
            ) : null}
          </div>
        ))}
      </div>

      {showModal && editItem && !readOnly ? (
        <>
          {/* biome-ignore lint/a11y/useKeyWithClickEvents: overlay modal */}
          <div
            className="fixed inset-0 z-50 flex items-center justify-center bg-black/50"
            onClick={() => setShowModal(false)}
          >
            <div
              className="w-full max-w-lg max-h-[90vh] overflow-y-auto rounded-xl bg-white p-6 shadow-xl"
              onClick={(e) => e.stopPropagation()}
              onKeyDown={(e: KeyboardEvent) => e.stopPropagation()}
            >
              <div className="mb-4 flex items-center justify-between">
                <h3 className="font-semibold text-slate-900">Éditer l'élément</h3>
                <button
                  type="button"
                  onClick={() => setShowModal(false)}
                  className="text-slate-400 hover:text-slate-600"
                >
                  <X className="h-5 w-5" />
                </button>
              </div>
              <div className="space-y-4">
                <label className="flex flex-col gap-1 text-sm font-medium text-slate-700">
                  Titre *
                  <input
                    type="text"
                    className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm font-normal outline-none focus:border-emerald-500"
                    value={editItem.title}
                    onChange={(e) => setEditItem((p) => (p ? { ...p, title: e.target.value } : p))}
                  />
                </label>
                <label className="flex flex-col gap-1 text-sm font-medium text-slate-700">
                  Description
                  <textarea
                    className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm font-normal outline-none focus:border-emerald-500 min-h-[80px] resize-y"
                    value={editItem.description}
                    onChange={(e) =>
                      setEditItem((p) => (p ? { ...p, description: e.target.value } : p))
                    }
                  />
                </label>
                <label className="flex flex-col gap-1 text-sm font-medium text-slate-700">
                  Lien
                  <input
                    type="url"
                    className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm font-normal outline-none focus:border-emerald-500"
                    value={editItem.link}
                    onChange={(e) => setEditItem((p) => (p ? { ...p, link: e.target.value } : p))}
                    placeholder="https://"
                  />
                </label>

                <div className="space-y-2 rounded-lg border border-slate-100 bg-slate-50/80 p-3">
                  <div>
                    <p className="text-xs font-medium text-slate-700">Sites concernés</p>
                    <p className="mt-0.5 text-[11px] text-slate-500">
                      Obligatoire pour chaque élément renseigné avant « Sauvegarder ».
                      {document.strictValidation
                        ? ' Validation stricte : le titre est aussi requis.'
                        : ''}
                    </p>
                  </div>
                  {sitesLoading ? (
                    <p className="text-xs text-slate-500">Chargement des sites…</p>
                  ) : !workSites?.length ? (
                    <p className="text-xs text-amber-700">
                      Aucun site assigné à votre compte. Demandez à un administrateur de vous
                      affecter à un site.
                    </p>
                  ) : (
                    <div className="max-h-36 space-y-1.5 overflow-y-auto rounded-md border border-slate-200 bg-white p-2">
                      {workSites.map((site) => {
                        const checked = editItem.sites.some((s) => s.id === site.siteId);
                        return (
                          <label
                            key={site.siteId}
                            className="flex cursor-pointer items-start gap-2 rounded px-1 py-0.5 text-sm text-slate-700 hover:bg-slate-50"
                          >
                            <input
                              type="checkbox"
                              className="mt-0.5 h-4 w-4 shrink-0 rounded border-slate-300 text-emerald-600"
                              checked={checked}
                              onChange={(e) => toggleEditingSite(site, e.target.checked)}
                            />
                            <span className="font-normal">
                              {site.siteName}
                              {site.city ? (
                                <span className="text-slate-500"> · {site.city}</span>
                              ) : null}
                            </span>
                          </label>
                        );
                      })}
                    </div>
                  )}
                </div>

                <div className="space-y-2 rounded-lg border border-slate-100 bg-slate-50/80 p-3">
                  <div>
                    <p className="text-xs font-medium text-slate-700">Modules ISO concernés</p>
                    <p className="mt-0.5 text-[11px] text-slate-500">
                      Optionnel — modules ISO déjà assignés à vos sites.
                    </p>
                  </div>
                  {!workSites?.length ? (
                    <p className="text-xs text-slate-500">
                      Ajoutez d’abord des sites à votre compte pour lister les modules concernés.
                    </p>
                  ) : modulesLoading ? (
                    <p className="text-xs text-slate-500">Chargement des modules…</p>
                  ) : !moduleChoices.length ? (
                    <p className="text-xs text-slate-500">
                      Aucun module ISO assigné à vos sites. Configurez-les dans la page Modules.
                    </p>
                  ) : (
                    <div className="max-h-40 space-y-1.5 overflow-y-auto rounded-md border border-slate-200 bg-white p-2">
                      {moduleChoices.map((mod) => {
                        const checked = editItem.modules.some((m) => m.id === mod.id);
                        return (
                          <label
                            key={mod.id}
                            className="flex cursor-pointer items-start gap-2 rounded px-1 py-0.5 text-sm text-slate-700 hover:bg-slate-50"
                          >
                            <input
                              type="checkbox"
                              className="mt-0.5 h-4 w-4 shrink-0 rounded border-slate-300 text-emerald-600"
                              checked={checked}
                              onChange={(e) => toggleEditingModule(mod, e.target.checked)}
                            />
                            <span className="font-normal">
                              <span className="font-medium text-slate-800">{mod.code}</span>
                              <span className="text-slate-600"> — {mod.name}</span>
                            </span>
                          </label>
                        );
                      })}
                    </div>
                  )}
                </div>
              </div>
              <div className="mt-5 flex justify-end gap-3">
                <button
                  type="button"
                  onClick={() => setShowModal(false)}
                  className="rounded-lg border border-slate-200 px-4 py-2 text-sm font-medium text-slate-600 hover:bg-slate-50"
                >
                  Annuler
                </button>
                <button
                  type="button"
                  onClick={saveModal}
                  className="rounded-lg bg-emerald-500 px-4 py-2 text-sm font-medium text-white hover:bg-emerald-600"
                >
                  Confirmer
                </button>
              </div>
            </div>
          </div>
        </>
      ) : null}
    </div>
  );
}
